import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Home, 
  Store, 
  Book, 
  Users, 
  Package, 
  DollarSign, 
  Award, 
  ChevronLeft,
  ChevronRight,
  LogOut,
  HelpCircle,
  User,
  ShoppingCart,
  Building2,
  UserCheck,
  BarChart3,
  FileText,
  Bell,
  Megaphone,
  Truck,
  Mail,
  Phone,
  ClipboardList,
  ClipboardCheck,
  LayoutGrid
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { getAriaLabel, keyboardHandlers, trapFocus } from '../../utils/accessibility';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useShipmentNotifications } from '../../hooks/useShipmentNotifications';
import { useAssignedShipmentsCount } from '../../hooks/useAssignedShipmentsCount';
interface OffcanvasMenuProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  onSupportClick?: () => void;
}

const MenuContainer = styled.div<{ isCollapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height for mobile */
  width: ${props => props.isCollapsed ? '80px' : '280px'};
  background: ${ThemeColors.background.sidebar};
  border-right: 1px solid ${ThemeColors.border.light};
  transition: width ${Transitions.duration.standard} ${Transitions.easing.easeInOut}, transform ${Transitions.duration.standard} ${Transitions.easing.easeInOut};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: ${Shadows.sm};
  overflow-x: hidden; /* Prevent horizontal scrolling */
  
  @media (max-width: 768px) {
    transform: translateX(${props => props.isCollapsed ? '-100%' : '0'});
    width: ${props => props.isCollapsed ? '70px' : '250px'};
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    max-height: 100dvh;
  }
`;

const MenuHeader = styled.div<{ isCollapsed: boolean }>`
  padding: ${props => props.isCollapsed ? `${Spacing.sm} ${Spacing.sm}` : `${Spacing.sm} ${Spacing.md}`};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'space-between'};
  min-height: 70px;
  flex-shrink: 0; /* Prevent header from shrinking */
  background: ${ThemeColors.background.sidebar};
  
  @media (max-width: 768px) {
    min-height: 70px;
    position: relative;
    z-index: 1;
  }
`;

const LogoContainer = styled.div<{ isCollapsed: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  align-items: center;
  justify-content: flex-start;
  flex: 1;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  padding: ${Spacing.xs};
  border-radius: ${BorderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const MenuItems = styled.div`
  flex: 1;
  min-height: 0; /* Important for flex children to be scrollable */
  padding: ${Spacing.xs} ${Spacing.sm};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${ThemeColors.gray[300]};
    border-radius: ${BorderRadius.md};
  }

  @media (max-width: 768px) {
    /* On mobile, ensure it's scrollable and accounts for header/footer */
    max-height: calc(100dvh - 70px - 60px); /* Account for header (~70px) and footer (~60px) */
    min-height: 0;
  }
`;

const MenuSection = styled.div<{ isCollapsed: boolean }>`
  margin-bottom: ${Spacing.sm};
  
  ${props => !props.isCollapsed && `
    &:not(:first-child) {
      margin-top: ${Spacing.sm};
    }
  `}
`;

const SectionTitle = styled.h3<{ isCollapsed: boolean }>`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: ${Typography.letterSpacing.wide};
  padding: ${props => props.isCollapsed ? `${Spacing.xs} 0` : `${Spacing.xs} ${Spacing.sm}`};
  margin-bottom: ${Spacing.xs};
  display: ${props => props.isCollapsed ? 'none' : 'block'};
`;

const MenuItem = styled.button<{ isCollapsed: boolean; isActive?: boolean }>`
  width: 100%;
  background: ${props => props.isActive ? ThemeColors.primary.light + '33' : 'transparent'};
  border: none;
  color: ${props => props.isActive ? ThemeColors.primary.main : ThemeColors.text.primary};
  padding: ${props => props.isCollapsed ? `${Spacing.sm} 0` : `${Spacing.sm}`};
  margin: ${props => props.isCollapsed ? '1px 0' : '1px 0'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.isCollapsed ? '0' : Spacing.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  position: relative;
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};

  &:hover {
    background-color: ${props => props.isActive ? ThemeColors.primary.light + '4D' : ThemeColors.gray[100]};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
  
  ${props => props.isActive && `
    &:before {
      content: '';
      position: absolute;
      left: ${props.isCollapsed ? '-8px' : '0'};
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background-color: ${ThemeColors.primary.main};
      border-radius: 0 ${BorderRadius.sm} ${BorderRadius.sm} 0;
    }
  `}
`;

const MenuItemLink = styled.a<{ isCollapsed: boolean; isActive?: boolean }>`
  width: 100%;
  background: ${props => props.isActive ? ThemeColors.primary.light + '33' : 'transparent'};
  border: none;
  color: ${props => props.isActive ? ThemeColors.primary.main : ThemeColors.text.primary};
  padding: ${props => props.isCollapsed ? `${Spacing.sm} 0` : `${Spacing.sm}`};
  margin: ${props => props.isCollapsed ? '1px 0' : '1px 0'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.isCollapsed ? '0' : Spacing.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  position: relative;
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  text-decoration: none;

  &:hover {
    background-color: ${props => props.isActive ? ThemeColors.primary.light + '4D' : ThemeColors.gray[100]};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
  
  ${props => props.isActive && `
    &:before {
      content: '';
      position: absolute;
      left: ${props.isCollapsed ? '-8px' : '0'};
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background-color: ${ThemeColors.primary.main};
      border-radius: 0 ${BorderRadius.sm} ${BorderRadius.sm} 0;
    }
  `}
`;

const MenuItemIcon = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  color: ${props => props.isActive ? ThemeColors.primary.main : ThemeColors.text.secondary};
`;

const MenuItemLabel = styled.span<{ isCollapsed: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'block'};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.sm};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MenuItemBadge = styled.span`
  margin-left: auto;
  background: ${ThemeColors.error.main};
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  min-width: 18px;
  text-align: center;
`;

const CollapsedBadgeDot = styled.span`
  position: absolute;
  top: 4px;
  right: 8px;
  width: 10px;
  height: 10px;
  background: ${ThemeColors.error.main};
  border-radius: 50%;
  border: 2px solid ${ThemeColors.background.sidebar};
`;

const Tooltip = styled.div<{ isCollapsed: boolean; show: boolean }>`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: ${ThemeColors.secondary.main};
  color: ${ThemeColors.secondary.contrastText};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  white-space: nowrap;
  z-index: 1001;
  opacity: ${props => props.isCollapsed && props.show ? 1 : 0};
  visibility: ${props => props.isCollapsed && props.show ? 'visible' : 'hidden'};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeOut};
  margin-left: ${Spacing.md};
  box-shadow: ${Shadows.md};
  pointer-events: ${props => props.isCollapsed && props.show ? 'auto' : 'none'};

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: ${ThemeColors.secondary.main};
  }
`;

const MenuFooter = styled.div<{ isCollapsed: boolean }>`
  padding: ${Spacing.xs} ${props => props.isCollapsed ? Spacing.xs : Spacing.sm};
  border-top: 1px solid ${ThemeColors.border.light};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  flex-shrink: 0; /* Prevent footer from shrinking */
  background: ${ThemeColors.background.sidebar};
  
  @media (max-width: 768px) {
    position: relative;
    z-index: 1;
  }
`;

const HelpText = styled.div<{ isCollapsed: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} 0;
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
  
  svg {
    min-width: 16px;
  }
`;

const HelpItem = styled.button<{ isCollapsed: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} 0;
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
  transition: all 0.2s ease;
  border-radius: ${BorderRadius.sm};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.primary.main};
  }

  svg {
    min-width: 16px;
  }
  
  ${props => props.isCollapsed && `
    justify-content: center;
    padding: ${Spacing.sm} 0;
  `}
`;

const SupportSection = styled.div<{ isCollapsed: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  margin: ${Spacing.xs} 0;
  border: 1px solid ${ThemeColors.border.light};
`;

const SupportTitle = styled.h4`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SupportLink = styled.a`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.primary.main};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: 2px 0;
  
  &:hover {
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
  }
`;

const SupportButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.primary.main};
  text-decoration: underline;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  cursor: pointer;
  margin-top: ${Spacing.xs};
  font-weight: ${Typography.fontWeight.medium};

  &:hover {
    color: ${ThemeColors.primary.dark};
  }
`;

/**
 * Menu item definitions - different menus for managers vs sales reps
 * @param pathPrefix - Path prefix based on user role (/manager or '')
 * @param t - Translation function
 * @param userRole - User role ('manager' or 'salesRep')
 * @param userEmail - Signed-in email, used to expose in-testing tools
 */
const getMenuItems = (pathPrefix: string = '', t: (key: string) => string, userRole: string = 'salesRep', userEmail: string = '') => {
  const hasManagerAccess = userRole === 'manager' || userRole === 'hand-lead' || userRole === 'admin';
  const isHandLead = userRole === 'hand-lead';
  const showPlanogramTool = Boolean(userEmail.trim());
  const planogramItem = { id: 'planogram-order', label: 'Planogram Ordering', icon: LayoutGrid, path: '/planogram-order' };
  
  if (hasManagerAccess) {
    const main = [
      { id: 'dashboard', label: t('navigation.dashboard'), icon: Home, path: `${pathPrefix}/dashboard` },
      { id: 'orders', label: 'Order Management', icon: ShoppingCart, path: `${pathPrefix}/orders` },
      { id: 'products', label: 'Product Management', icon: Package, path: `${pathPrefix}/products` },
      { id: 'stores', label: 'Store Management', icon: Building2, path: `${pathPrefix}/stores` },
      { id: 'reports', label: 'Analytics & Performance', icon: BarChart3, path: `${pathPrefix}/analytics` },
    ];

    const operations = [];
    
    if (!isHandLead) {
      operations.push({ id: 'users', label: 'User Management', icon: UserCheck, path: `${pathPrefix}/users` });
    }
    
    if (isHandLead) {
      operations.push({ id: 'store-resource', label: 'Resource Library', icon: Book, path: '/resources' });
    } else {
      operations.push({ id: 'store-resource', label: 'Resource Library', icon: Book, path: `${pathPrefix}/resources` });
    }
    
    operations.push({ id: 'store-visit', label: t('navigation.storeVisit'), icon: Store, path: `${pathPrefix}/store-visit` });
    
    if (isHandLead) {
      operations.push({ id: 'shipments', label: 'Shipments', icon: Truck, path: '/shipments' });
    } else {
      operations.push({ id: 'shipments', label: 'Shipments', icon: Truck, path: `${pathPrefix}/shipments` });
    }

    const tools = [
      { id: 'payroll', label: t('navigation.payroll'), icon: DollarSign, path: `${pathPrefix}/payroll` },
      { id: 'contest-entry', label: t('navigation.contestEntry'), icon: Award, path: `${pathPrefix}/contest-entry` },
      { id: 'eos-survey', label: 'EOS Survey', icon: ClipboardList, path: `${pathPrefix}/eos-survey` },
      { id: 'tradeshow', label: 'Trade Show Orders', icon: ClipboardCheck, path: `${pathPrefix}/tradeshow` },
      { id: 'announcements', label: 'Announcements', icon: Megaphone, path: `${pathPrefix}/announcements` },
    ];

    if (showPlanogramTool) {
      tools.push(planogramItem);
    }

    const account = [
      { id: 'profile', label: t('navigation.profile'), icon: User, path: `${pathPrefix}/profile` },
      { id: 'logout', label: t('navigation.logout'), icon: LogOut, path: '/logout' },
    ];

    return { main, operations, tools, account };
  }

  const main = [
    { id: 'dashboard', label: t('navigation.dashboard'), icon: Home, path: `${pathPrefix}/dashboard` },
    { id: 'store-visit', label: t('navigation.storeVisit'), icon: Store, path: `${pathPrefix}/store-visit` },
    { id: 'shipments', label: 'Shipments', icon: Truck, path: `${pathPrefix}/shipments` },
    { id: 'store-resource', label: t('navigation.storeResource'), icon: Book, path: `${pathPrefix}/resources` },
    { id: 'announcements', label: 'Announcements', icon: Megaphone, path: `${pathPrefix}/announcements` },
    { id: 'payroll', label: t('navigation.payroll'), icon: DollarSign, path: `${pathPrefix}/payroll` },
    { id: 'contest-entry', label: t('navigation.contestEntry'), icon: Award, path: `${pathPrefix}/contest-entry` },
    { id: 'eos-survey', label: 'EOS Survey', icon: ClipboardList, path: `${pathPrefix}/eos-survey` },
  ];

  if (showPlanogramTool) {
    main.push(planogramItem);
  }

  const account = [
    { id: 'profile', label: t('navigation.profile'), icon: User, path: `${pathPrefix}/profile` },
    { id: 'logout', label: t('navigation.logout'), icon: LogOut, path: '/logout' },
  ];

  return { main, account };
};

export const OffcanvasMenu: React.FC<OffcanvasMenuProps> = ({ 
  isCollapsed, 
  onToggle, 
  onNavigate,
  onSupportClick
}) => {
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<string>('store-visit'); // Default to store-visit
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { userRole, user } = useAuth();
  const location = useLocation();
  
  // Get announcement unread count for badge
  const { unreadCount } = useAnnouncements();
  
  // Get shipment notification unread count (for notifications)
  const { unreadCount: shipmentUnreadCount } = useShipmentNotifications();
  
  // Get assigned shipments count (unconfirmed, non-deleted) for badge (sales reps only)
  const { count: assignedShipmentsCount } = useAssignedShipmentsCount();
  
  // Determine path prefix based on user role (manager or hand-lead)
  const hasManagerAccess = userRole === 'manager' || userRole === 'hand-lead' || userRole === 'admin';
  const pathPrefix = hasManagerAccess ? '/manager' : '';

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);
  
  // Set active item based on current location
  useEffect(() => {
    // Extract the current path without the prefix
    const currentPath = location.pathname;
    const pathWithoutPrefix = currentPath.replace(pathPrefix, '');
    
    // Get all menu items based on user role
    const menuSections = getMenuItems(pathPrefix, t, userRole || 'salesRep', user?.email || '');
    const allMenuItems = Object.values(menuSections).flat();
    
    const matchingItem = allMenuItems.find(item => {
      // Handle special case for root path
      if (currentPath === '/' || currentPath === '/manager') {
        return item.id === 'dashboard';
      }
      // Handle other paths
      return item.path === currentPath;
    });
    
    if (matchingItem) {
      setActiveItem(matchingItem.id);
    }
  }, [location.pathname, pathPrefix, userRole, t]);

  const handleMenuItemClick = (id: string, path: string) => {
    setActiveItem(id);
    onNavigate(path);
    if (isMobile) {
      onToggle(); // Close menu on mobile after navigation
    }
  };

  const renderMenuItems = (items: any[], section: string) => {
    return items.map((item) => {
      const IconComponent = item.icon;
      const isActive = activeItem === item.id;
      const uniqueItemId = `${section}-${item.id}`;
      
      // Show badge for announcements or shipments
      const showAnnouncementBadge = item.id === 'announcements' && unreadCount > 0;
      // For shipments, show count of unconfirmed assigned shipments (sales reps only)
      const showShipmentBadge = item.id === 'shipments' && assignedShipmentsCount > 0 && userRole === 'sales-representative';
      const badgeCount = showAnnouncementBadge ? unreadCount : showShipmentBadge ? assignedShipmentsCount : 0;
      const showBadge = showAnnouncementBadge || showShipmentBadge;
      
      // Use anchor tag for navigation links (not logout) to enable browser context menu
      const isNavigationLink = item.id !== 'logout';
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'OffcanvasMenu.tsx:508',message:'commonProps creation - before',data:{uniqueItemId,hasKey:true,itemId:item.id,section},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      // Extract key from commonProps - React requires key to be passed directly, not via spread
      const commonProps = {
        isCollapsed,
        isActive,
        onMouseEnter: () => setHoveredItem(uniqueItemId),
        onMouseLeave: () => setHoveredItem(null),
        'aria-label': item.label,
        role: 'menuitem' as const,
      };
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'OffcanvasMenu.tsx:516',message:'commonProps created - key extracted',data:{commonPropsKeys:Object.keys(commonProps),hasKeyProp:'key' in commonProps,uniqueItemId,isNavigationLink},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      const menuItemContent = (
        <>
          <MenuItemIcon isActive={isActive}>
            <IconComponent size={20} aria-hidden="true" />
          </MenuItemIcon>
          <MenuItemLabel isCollapsed={isCollapsed}>
            {item.label}
          </MenuItemLabel>
          {showBadge && !isCollapsed && (
            <MenuItemBadge>{badgeCount > 99 ? '99+' : badgeCount}</MenuItemBadge>
          )}
          {showBadge && isCollapsed && (
            <CollapsedBadgeDot aria-label={`${badgeCount} unread`} />
          )}
          <Tooltip 
            isCollapsed={isCollapsed} 
            show={hoveredItem === uniqueItemId}
            role="tooltip"
          >
            {item.label}
          </Tooltip>
        </>
      );
      
      if (isNavigationLink) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'OffcanvasMenu.tsx:543',message:'Rendering MenuItemLink - key passed directly',data:{uniqueItemId,itemId:item.id,commonPropsHasKey:'key' in commonProps},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        return (
          <MenuItemLink
            key={uniqueItemId}
            {...commonProps}
            href={item.path}
            onClick={(e) => {
              // Only prevent default for regular left clicks (no modifier keys)
              // Allow Ctrl/Cmd+click, Shift+click, and middle-click to work naturally
              // Right-click context menu will work automatically with anchor tags
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                e.preventDefault();
                handleMenuItemClick(item.id, item.path);
              }
              // For modifier keys, let the browser handle it naturally (opens in new tab/window)
            }}
          >
            {menuItemContent}
          </MenuItemLink>
        );
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'OffcanvasMenu.tsx:562',message:'Rendering MenuItem - key passed directly',data:{uniqueItemId,itemId:item.id,commonPropsHasKey:'key' in commonProps},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        // Use button for logout and other non-navigation items
        return (
          <MenuItem
            key={uniqueItemId}
            {...commonProps}
            onClick={() => handleMenuItemClick(item.id, item.path)}
          >
            {menuItemContent}
          </MenuItem>
        );
      }
    });
  };

  // Get menu items based on user role
  const menuSections = getMenuItems(pathPrefix, t, userRole || 'salesRep', user?.email || '');

  return (
    <MenuContainer isCollapsed={isCollapsed}>
      <MenuHeader isCollapsed={isCollapsed}>
        {!isCollapsed && (
          <LogoContainer isCollapsed={isCollapsed}>
            <LogoImage src="/assets/erp-logo.svg" alt="ERP Mock Logo" />
          </LogoContainer>
        )}
        <ToggleButton 
          onClick={onToggle} 
          aria-label={getAriaLabel('Toggle', 'menu', isCollapsed ? 'collapsed' : 'expanded')}
          aria-expanded={!isCollapsed}
          aria-controls="main-navigation"
        >
          {isCollapsed ? <ChevronRight size={20} aria-hidden="true" /> : <ChevronLeft size={20} aria-hidden="true" />}
        </ToggleButton>
      </MenuHeader>

      <MenuItems id="main-navigation" role="navigation" aria-label="Main navigation">
        {/* Main Section */}
        <MenuSection isCollapsed={isCollapsed}>
          {hasManagerAccess && <SectionTitle isCollapsed={isCollapsed}>Main</SectionTitle>}
          {renderMenuItems(menuSections.main, 'main')}
        </MenuSection>
        
        {/* Manager-specific sections (includes hand-lead) */}
        {hasManagerAccess && (
          <>
            <MenuSection isCollapsed={isCollapsed}>
              <SectionTitle isCollapsed={isCollapsed}>Operations</SectionTitle>
              {renderMenuItems(menuSections.operations || [], 'operations')}
            </MenuSection>
            
            <MenuSection isCollapsed={isCollapsed}>
              <SectionTitle isCollapsed={isCollapsed}>Tools</SectionTitle>
              {renderMenuItems(menuSections.tools || [], 'tools')}
            </MenuSection>
          </>
        )}
        
        {/* Account Section (both roles) */}
        <MenuSection isCollapsed={isCollapsed} style={{ marginTop: 'auto' }}>
          <SectionTitle isCollapsed={isCollapsed}>Account</SectionTitle>
          {renderMenuItems(menuSections.account, 'account')}
        </MenuSection>
      </MenuItems>
      
      <MenuFooter isCollapsed={isCollapsed}>
        {!isCollapsed ? (
          <SupportSection isCollapsed={isCollapsed}>
            <SupportTitle>
              <HelpCircle size={14} />
              Technical Support
            </SupportTitle>
            <SupportLink href="mailto:devbob.co@gmail.com">
              <Mail size={12} />
              devbob.co@gmail.com
            </SupportLink>
            <SupportLink href="tel:5794235350">
              <Phone size={12} />
              579-423-5350
            </SupportLink>
            <SupportButton onClick={(e) => { e.preventDefault(); onSupportClick?.(); }}>
              <FileText size={12} />
              Open Support Form
            </SupportButton>
          </SupportSection>
        ) : (
          <HelpItem 
            isCollapsed={isCollapsed} 
            onClick={onSupportClick}
            aria-label="Technical Support"
          >
            <HelpCircle size={20} />
          </HelpItem>
        )}
      </MenuFooter>
    </MenuContainer>
  );
};